import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { createActionHighlightSuccess, getActionByName, getActionByNameSuccess, getBcPcRevStages } from "../../../store/actions/action.Highlighters";
import { deleteActionApi } from "../../../store/api";
import { getAllSubtask, getAllSubtaskSummary } from "../../../store/actions/project.action";
import { useDispatch } from "react-redux";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Badge, Button, Card, Flex, Link, Spinner, Text, useColorMode, Box } from "theme-ui";
import CreateBC from "./CreateBC";
import deleteIcon from "../../assets/images/Icon/delete.svg";
import ActionBcPcChecklist from "./ActionBcPcChecklist";
import { RootState, TaskEstimate, ActionRowData, TaskOption, DetermineNameStyleProps, ActionBackgroundCheckProps, ActionDeleteCellProps, DeleteActionApiResponse, DeleteActionApiError } from "../../../../@types";
import CustomTooltip from "../../CustomTooltip";
import { FaLink, FaPlus } from "react-icons/fa";
import { rolePermissionsHighLighters } from "../../../utils/CustomHandler";
import DataTable from "../../Tabels/CommonBCPCRETable";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useParams, useLocation } from "react-router-dom";
import theme from "../../../theme";
import { convertMsToHHMM, checkRevisionDeletionPermission } from "../../../utils/GenerateQueryString";
import toast from 'react-hot-toast';

const ActionDeleteCell: React.FC<ActionDeleteCellProps> = ({ row, selectedHighlight, handleDeleteAction }) => {
    const actionType = row?.original?.actionType || row?.original?.payload?.actionType || selectedHighlight?.name;

    return (
        <Flex sx={{ justifyContent: 'center', alignItems: 'center' }}>
            <Box
                onClick={() => handleDeleteAction(row?.original?._id, actionType, row)}
                sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
                <img src={deleteIcon} alt="delete" style={{ width: '23px', height: '23px', cursor: 'pointer' }} />
            </Box>
        </Flex>
    );
};



// Helper: determine color and fontWeight for Name cell based on row, current task (tasksView), and route
function determineNameStyle({ row, tasksView, taskOptions, location, status, taskStatus, colorMode }: DetermineNameStyleProps) {
    const payload = row?.original?.payload || {};
    const taskEstimateList = Array.isArray(payload?.taskEstimateList) ? payload.taskEstimateList : [];
    const displayedTaskIds = Array.isArray(payload?.taskId) ? payload.taskId : [];
    const isDeliveredCompleted = payload?.isCompleted === true;
    const rowStatus = row?.original?.status || status || taskStatus;
    // build estimate map for quick lookup
    const estimateMap: Record<string, TaskEstimate> = {};
    for (const e of taskEstimateList) {
        if (e && e.taskId) estimateMap[e.taskId] = e;
    }

    const openedFromEditTask = typeof location?.pathname === 'string' && location.pathname.includes('/edit-task');
    const currentTaskId = tasksView?._id || (typeof tasksView?.taskId === 'string' ? tasksView.taskId : (tasksView?.taskId as { _id: string })?._id) || null;

    //  if completed, always light black
    if (isDeliveredCompleted) {
        return { color: colorMode === 'light' ? 'black' : 'white', fontWeight: 200, textDecoration: "none" };
    }
    // task-view rules when opened from edit-task and current task belongs to this revision
    else if (openedFromEditTask && currentTaskId && displayedTaskIds.includes(currentTaskId)) {
        const estimateObj = estimateMap[currentTaskId] || null;
        const estimateValue = Number(estimateObj?.estimatedTime || 0);
        const isTaskDone = !!(estimateObj && estimateObj.isTaskDone === true);

        if (estimateValue === 0) {
            return { color: 'red', fontWeight: 500 };
        } else if (estimateValue > 0 && !isTaskDone) {
            return { color: colorMode === 'light' ? 'black' : 'white', fontWeight: 600 };
        } else if (isTaskDone) {
            return { color: 'green', fontWeight: 400 };
        } else {
            return { color: colorMode === 'light' ? 'black' : 'white', fontWeight: 400 };
        }
    }

    else {
        const allEstimatesAreZero = Array.isArray(taskEstimateList) && taskEstimateList.length > 0 && taskEstimateList.every((t: TaskEstimate) => Number(t?.estimatedTime) === 0);
        const allTasksDone = Array.isArray(displayedTaskIds) && displayedTaskIds.length > 0 && displayedTaskIds.every((taskId: string) => {
            const estimate = estimateMap[taskId];
            return !!(estimate && estimate.isTaskDone === true);
        });
        const allEstimatesGreaterThanZero = Array.isArray(displayedTaskIds) && displayedTaskIds.length > 0 && displayedTaskIds.every((taskId: string) => {
            const estimate = estimateMap[taskId];
            return !!(estimate && Number(estimate.estimatedTime) > 0);
        });
        const anyMissingOrZeroEstimate = Array.isArray(displayedTaskIds) && displayedTaskIds.length > 0 && displayedTaskIds.some((taskId: string) => {
            const estimate = estimateMap[taskId];
            return !(estimate && Number(estimate.estimatedTime) > 0);
        });

        if (allTasksDone) {
            return { color: 'green', fontWeight: 400 };
        } else if (Array.isArray(displayedTaskIds) && displayedTaskIds.length > 0 && anyMissingOrZeroEstimate) {
            return { color: 'red', fontWeight: 500 };
        } else if (allEstimatesGreaterThanZero && rowStatus !== 'Ready To Deliver') {
            return { color: colorMode === 'light' ? 'black' : 'white', fontWeight: 600 };
        } else if (allEstimatesAreZero) {
            return { color: 'red', fontWeight: 500 };
        } else {
            return { color: colorMode === 'light' ? 'black' : 'white', fontWeight: 400, textDecoration: "none" };
        }
    }
}

const ActionBackgroundCheck = ({ Open, setOpen, selectedHighlight, actionObject, keys, showAddNewButton = true, status, taskStatus, stageStatus }: ActionBackgroundCheckProps) => {
    const { auther } = useSelector((state: RootState) => state.auth);
    const is_Super_Admin = auther?.Role?.map(role => role?.RoleName).some(role => role?.toLocaleLowerCase() === "super admin");
    const is_Task_Worker = auther?.Role?.map(role => role?.RoleName).every(role => role.toLocaleLowerCase() === "task worker");
    const { actionnameList, createHighlight, loader, BcPclist } = useSelector((state: RootState) => (state as unknown as { actionHighlight: { actionnameList: string[]; createHighlight: { data?: { _id: string } }; loader: boolean; BcPclist: (ActionRowData & { stageId: { name: string; color?: string, temporaryName?: string } })[] } }).actionHighlight)
    const [showCreateBCModal, setShowCreateBCModal] = useState<boolean>(false);
    const [showActionBcPcChecklistModal, setShowActionBcPcChecklistModal] = useState<boolean>(false);
    const [actionId, setActionId] = useState("");
    const { stageId } = useParams<{ stageId: string }>();
    const location = useLocation();
    const showDeleteColumn = typeof location?.pathname === 'string' && (location.pathname.includes('/edit-stages') || location.pathname.includes('/edit-task'));

    const { taskbyproject, tasksView } = useSelector((state: RootState) => (state as unknown as { project: { taskbyproject: { _id: string; taskId: { name: string; _id: string } }[]; tasksView: { _id?: string; taskId?: string | { _id: string } } } }).project);

    const projectId = actionObject?.projectId;
    const taskOptions = taskbyproject?.map((obj: { taskId: { name: string }, _id: string }) => ({
        label: obj?.taskId?.name,
        value: obj?._id,
    }));

    const openedFromEditTask = typeof location?.pathname === 'string' && location.pathname.includes('/edit-task');
    const currentTaskId = tasksView?._id || (typeof tasksView?.taskId === 'string' ? tasksView.taskId : (tasksView?.taskId as { _id: string })?._id) || null;

    const filteredBcPclist = BcPclist?.filter((curr: ActionRowData & { stageId: { name: string } }) => {
        if (openedFromEditTask && currentTaskId) {
            const taskIds = curr?.payload?.taskId;
            const isCurrentTask = Array.isArray(taskIds) && taskIds.includes(currentTaskId);
            const isRevStage = curr?.stageId?.name?.toUpperCase() === 'REV';
            return isCurrentTask || isRevStage;
        }
        return true;
    });

    const groupedData = filteredBcPclist?.reduce((acc: Record<string, ActionRowData[]>, curr: ActionRowData & { stageId: { name: string, temporaryName?: string } }) => {
        const stageName = curr.stageId?.temporaryName || curr.stageId?.name;
        if (!acc[stageName]) {
            acc[stageName] = [];
        }
        acc[stageName].push(curr);
        return acc;
    }, {});

    const dispatch = useDispatch();
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
    const [selectedDeleteId, setSelectedDeleteId] = useState<string | null>(null);
    const [selectedDeleteType, setSelectedDeleteType] = useState<string | null>(null);
    const [selectedDeleteRow, setSelectedDeleteRow] = useState<Row<ActionRowData> | null>(null);
    const [deleteConfirmMessage, setDeleteConfirmMessage] = useState<string>('');
    const [isInitialConfirm, setIsInitialConfirm] = useState<boolean>(true);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const handleDeleteAction = (id: string | null, type?: string | null, row?: Row<ActionRowData>) => {
        const item = row?.original;
        const userRoles = auther?.Role || [];
        const itemStatus = item?.status || status || taskStatus || stageStatus;

        const checkResult = checkRevisionDeletionPermission(item, userRoles, itemStatus);

        if (!checkResult.allowed) {
            toast.error(checkResult.message);
            return;
        }

        setSelectedDeleteId(id);
        setSelectedDeleteType(type || null);
        setSelectedDeleteRow(row || null);
        setDeleteConfirmMessage(checkResult.message);
        setIsInitialConfirm(true);
        setShowDeleteConfirmation(true);
    };

    const confirmDeleteAction = () => {
        if (!selectedDeleteId || isDeleting) return;
        setIsDeleting(true);

        const openedFromEditTask = typeof location?.pathname === 'string' && location.pathname.includes('/edit-task');
        const currentTaskId = tasksView?._id || (typeof tasksView?.taskId === 'string' ? tasksView.taskId : (tasksView?.taskId as { _id: string })?._id) || null;
        const targetTaskId = openedFromEditTask && currentTaskId ? currentTaskId : undefined;

        deleteActionApi(selectedDeleteId, selectedDeleteType || "", targetTaskId, isInitialConfirm ? undefined : true)
            .then((res: DeleteActionApiResponse) => {
                const isConfirmReq = res?.data?.confirmationRequired === true || res?.confirmationRequired === true;
                if (isConfirmReq) {
                    const resData = res?.data || res;
                    const msg = resData?.message || `This revision task has spent time. Are you sure you want to delete it?`;
                    setDeleteConfirmMessage(msg);
                    setIsInitialConfirm(false);
                    setIsDeleting(false);
                } else {
                    dispatch(getActionByName(actionObject, { name: selectedHighlight?.name }));
                    dispatch(getBcPcRevStages(projectId, { name: selectedHighlight?.name }));
                    if (actionObject && actionObject?.projectId && actionObject?.stageId && actionObject?.taskId) {
                        dispatch(getAllSubtask(actionObject));
                        dispatch(getAllSubtaskSummary(actionObject));
                    }
                    toast.success("Revision task deleted successfully");
                    setIsDeleting(false);
                    setShowDeleteConfirmation(false);
                    setSelectedDeleteId(null);
                    setSelectedDeleteType(null);
                    setSelectedDeleteRow(null);
                }
            })
            .catch((err: DeleteActionApiError) => {
                const errMsg = err?.response?.data?.message || err?.message || "Failed to delete revision task";
                toast.error(errMsg);
                setIsDeleting(false);
                setShowDeleteConfirmation(false);
                setSelectedDeleteId(null);
                setSelectedDeleteType(null);
                setSelectedDeleteRow(null);
            });
    };

    const cancelDeleteStage = () => {
        if (isDeleting) return;
        setShowDeleteConfirmation(false);
        setSelectedDeleteId(null);
        setSelectedDeleteRow(null);
    };

    const togglePopover = () => {
        setOpen(!Open);
        setShowCreateBCModal(false);
        setShowActionBcPcChecklistModal(false);
        setActionId("");
    }
    const handleDeliverClick = () => {
        (is_Task_Worker && rolePermissionsHighLighters.includes(selectedHighlight?.name)) ? setShowCreateBCModal(false) : setShowCreateBCModal(true);

    };

    const handleTemplateClick = (id: string) => {
        setActionId(id)
        setShowActionBcPcChecklistModal(true);
    };

    useEffect(() => {
        dispatch(createActionHighlightSuccess([]))
    }, [])

    useEffect(() => {
        if (createHighlight?.data?._id) {
            setActionId(createHighlight?.data?._id)
            setShowActionBcPcChecklistModal(true)
            setShowCreateBCModal(false);
        }
    }, [createHighlight?.data?._id])

    useEffect(() => {
        if (Open) {
            dispatch(getActionByName(actionObject, { name: selectedHighlight?.name }))
            dispatch(getBcPcRevStages(projectId, { name: selectedHighlight?.name }))
        }
    }, [selectedHighlight, Open, createHighlight])



    useEffect(() => {
        if (Open) {
            setShowActionBcPcChecklistModal(false)
        }

        return () => {
            dispatch(getActionByNameSuccess([]))
            dispatch(createActionHighlightSuccess([]))
        }

    }, [Open])
    const { paginations } = useSelector((state: RootState) => (state as unknown as { pagination: { paginations: { currentPage: number; limit: number } } }).pagination);
    const [colorMode] = useColorMode();
    const modalHeader = colorMode === 'light' ? "#fff" : "#20283c";
    const titleColor = (colorMode === "light" ? '#0a0a0a' : "#8d8d8d") as string;

    const columns = useMemo<ColumnDef<ActionRowData>[]>(() => [
        {
            header: 'Sr No.',
            enableColumnFilter: false,
            accessorKey: "no",
            cell: ({ row }) => {
                return (
                    <Text variant='Poppins14Normal24lh'>{((paginations?.currentPage - 1) * paginations?.limit) + row?.index + 1}</Text>
                )
            }
        },
        {
            accessorKey: "bc_pc_revision_name",
            header: 'Name',
            cell: ({ row }) => {
                const { color, fontWeight, textDecoration } = determineNameStyle({ row, tasksView, taskOptions, location, status, taskStatus, colorMode });
                const title = row?.original?.payload?.name;
                return (
                    <Text title={row?.original?.payload?.name} sx={{ textDecoration: textDecoration || "underline", cursor: "pointer", fontWeight }} style={{ color }} variant='Poppins14Normal24lh' onClick={() => handleTemplateClick(row?.original?._id)}>
                        {row?.original?.payload?.name.slice(0, 20)}
                    </Text>
                )
            },
        },

        {
            header: 'Major/Minor',
            accessorKey: "major/minor",
            cell: ({ row }) => {
                return (
                    <Text variant='Poppins14Normal24lh'>{row?.original?.payload?.majorMinor}</Text>
                )
            }
        },
        {
            header: 'Tasks',
            accessorKey: "tasks",
            cell: ({ row }) => {
                const matchingTaskNames: string[] = [];
                if (taskOptions) {

                    for (const task of taskOptions) {
                        if (row?.original?.payload?.taskId?.includes(task?.value)) {
                            matchingTaskNames?.push(task?.label);
                        }
                    }
                }

                return (
                    <Flex sx={{ flexDirection: 'column', pl: 2 }}>
                        {matchingTaskNames?.map((taskName: string, index: number) => (
                            <Flex key={index} sx={{ alignItems: 'center', mb: 1 }}>
                                <Text variant='Poppins14Normal24lh'>{taskName}</Text>
                            </Flex>
                        ))}
                    </Flex>
                )
            }
        },
        {
            header: 'Time Estimate',
            accessorKey: "taskEstimateList",
            cell: ({ row }) => {
                // Check if taskEstimateList exists in payload
                const taskIds = row?.original?.payload?.taskId || [];
                const taskEstimateList = row?.original?.payload?.taskEstimateList || [];
                if (!Array.isArray(taskIds) || !taskOptions) {
                    return <Text variant='Poppins14Normal24lh'>-</Text>
                }
                return (
                    <Flex sx={{ flexDirection: 'column' }}>
                        {taskOptions.filter((task: TaskOption) => taskIds.includes(task.value)).map((task: TaskOption) => {
                            const estimateObj = taskEstimateList.find((t: TaskEstimate) => t.taskId === task.value);
                            const timeValue = estimateObj && estimateObj.estimatedTime ? convertMsToHHMM(Number(estimateObj.estimatedTime)) : '00:00';

                            const getShortLabel = (name: string) => {
                                if (!name) return '';
                                const n = name.toLowerCase();

                                let trade: 'elect' | 'mech' | 'plumb' | 'other' = 'other';
                                if (n.includes('elect')) trade = 'elect';
                                else if (n.includes('mech')) trade = 'mech';
                                else if (n.includes('plumb')) trade = 'plumb';

                                const hyphenMatch = name.match(/-([A-Za-z0-9]+)-/);

                                switch (trade) {
                                    case 'elect': {
                                        if (n.includes('standard')) return 'E(';
                                        return 'E';
                                    }
                                    case 'mech': {
                                        if (n.includes('standard')) return 'M(';
                                        // parentheses like 'Mechanical (Zoning/Load Calcs)' -> M(Z
                                        const parenMatch = name.match(/\(([^)]+)\)/);
                                        if (parenMatch && parenMatch[1]) {
                                            const inside = parenMatch[1].trim();
                                            const firstToken = (inside.split(/[\/\s,]+/)[0] || '').charAt(0).toUpperCase();
                                            if (firstToken) return `M(${firstToken}`;
                                        }
                                        if (hyphenMatch && hyphenMatch[1]) return `M-${hyphenMatch[1].toUpperCase()}`;
                                        if (n.includes('energy') || n.includes('ene')) {
                                            if (n.includes('calc')) return 'M-C&';
                                            return 'M(C';
                                        }
                                        if (n.includes('revit')) return 'M-R';
                                        return 'M';
                                    }
                                    case 'plumb': {
                                        if (n.includes('standard')) return 'P(';
                                        return 'P';
                                    }
                                    default: {
                                        // fallback: use first character of first word
                                        return (name.split(' ')[0] || '').charAt(0).toUpperCase();
                                    }
                                }
                            }

                            const shortLabel = getShortLabel(task.label);

                            return (
                                <Text key={task.value} variant='Poppins14Normal24lh'>
                                    {shortLabel} : {timeValue}
                                </Text>
                            );
                        })}
                    </Flex>
                );
            }
        },
        {
            header: 'Time Spent',
            accessorKey: "taskEstimateList",
            cell: ({ row }) => {
                const taskIds = row?.original?.payload?.taskId || [];
                const taskEstimateList = row?.original?.payload?.taskEstimateList || [];
                if (!Array.isArray(taskIds) || !taskOptions) {
                    return <Text variant='Poppins14Normal24lh'>-</Text>
                }
                return (
                    <Flex sx={{ flexDirection: 'column' }}>
                        {taskOptions.filter((task: TaskOption) => taskIds.includes(task.value)).map((task: TaskOption) => {
                            const spentObj = taskEstimateList.find((t: TaskEstimate) => t.taskId === task.value);
                            const timeValue = spentObj && spentObj.spentTime ? convertMsToHHMM(Number(spentObj.spentTime)) : '00:00';

                            const getShortLabel = (name: string) => {
                                if (!name) return '';
                                const n = name.toLowerCase();

                                let trade: 'elect' | 'mech' | 'plumb' | 'other' = 'other';
                                if (n.includes('elect')) trade = 'elect';
                                else if (n.includes('mech')) trade = 'mech';
                                else if (n.includes('plumb')) trade = 'plumb';

                                const hyphenMatch = name.match(/-([A-Za-z0-9]+)-/);

                                switch (trade) {
                                    case 'elect':
                                        if (n.includes('standard')) return 'E(';
                                        return 'E';
                                    case 'mech': {
                                        if (n.includes('standard')) return 'M(';
                                        const parenMatch = name.match(/\(([^)]+)\)/);
                                        if (parenMatch && parenMatch[1]) {
                                            const inside = parenMatch[1].trim();
                                            const firstToken = (inside.split(/[\/\s,]+/)[0] || '').charAt(0).toUpperCase();
                                            if (firstToken) return `M(${firstToken}`;
                                        }
                                        if (hyphenMatch && hyphenMatch[1]) return `M-${hyphenMatch[1].toUpperCase()}`;
                                        if (n.includes('energy') || n.includes('ene')) {
                                            if (n.includes('calc')) return 'M-C&';
                                            return 'M(C';
                                        }
                                        if (n.includes('revit')) return 'M-R';
                                        return 'M';
                                    }
                                    case 'plumb': {
                                        if (n.includes('standard')) return 'P(';
                                        return 'P';
                                    }
                                    default:
                                        return (name.split(' ')[0] || '').charAt(0).toUpperCase();
                                }
                            }

                            const shortLabel = getShortLabel(task.label);

                            return (
                                <Text key={task.value} variant='Poppins14Normal24lh'>
                                    {shortLabel} : {timeValue}
                                </Text>
                            );
                        })}
                    </Flex>
                );
            }
        },
        {
            header: 'Link',
            accessorKey: "link",
            cell: ({ row }) => {
                return (
                    <Text variant="Poppins14Normal24lh">
                        {
                            Array?.isArray(row?.original?.payload?.emailLink) ? (
                                row?.original?.payload?.emailLink?.map((email: string, index: number) => (
                                    <Text title={email}>
                                        <Link sx={{ textDecoration: "none" }} key={index} target='_blank' href={email}>
                                            {/* {email} */}
                                            <FaLink />&nbsp;&nbsp;
                                        </Link>
                                    </Text>
                                ))
                            ) : (
                                null
                            )
                        }
                        {/* </Text> */}
                    </Text >
                )
            }
        },
        {
            header: 'Description',
            accessorKey: "description",
            cell: ({ row }) => {
                return (
                    <Text title={row?.original?.payload?.description} variant="Poppins14Normal24lh">{row?.original?.payload?.description.slice(0, 20) + (row?.original?.payload?.description.length > 20 ? " ..." : "")}</Text>
                )
            }
        },
        {
            header: 'Created At',
            accessorKey: "createdAt",
            cell: ({ row }: { row: Row<ActionRowData> }) => {
                return (
                    <Text variant="Poppins14Normal24lh">{row?.original?.createdAt?.split("T")?.[0]}</Text>
                )
            }
        },
        // Conditionally include Delete column only when on the /edit-stages route
        ...(showDeleteColumn ? [
            {
                header: 'Delete',
                accessorKey: "delete",
                cell: ({ row }: { row: Row<ActionRowData> }) => {
                    return (
                        <ActionDeleteCell row={row} selectedHighlight={selectedHighlight} handleDeleteAction={handleDeleteAction} />
                    );
                }
            }
        ] : [])
    ], [actionnameList, BcPclist, location?.pathname, tasksView, taskOptions, paginations?.currentPage, paginations?.limit, status, taskStatus, colorMode]);
    const closeButton = (
        <button
            type="button"
            aria-label="Close"
            onClick={togglePopover}
            style={{
                color: colorMode === 'dark' ? '#fff' : '#000',
                background: 'transparent',
                border: 'none',
                fontSize: 23,
                lineHeight: 1,
                padding: 0,
                cursor: 'pointer'
            }}
        >
            <span aria-hidden="true">&times;</span>
        </button>
    );
    return (
        <Modal isOpen={Open} scrollable toggle={togglePopover} size="lg" centered >

            <ModalHeader toggle={togglePopover} close={closeButton} style={{ backgroundColor: modalHeader }}>
                <Flex sx={{ gap: "1rem" }}><img src={selectedHighlight?.icon} /><Text sx={{ color: selectedHighlight?.color }}>{selectedHighlight?.name?.toUpperCase()}</Text></Flex></ModalHeader>

            <ModalBody style={{ backgroundColor: modalHeader }}>
                <Flex className="justify-content-end">
                    {showAddNewButton && (
                        is_Task_Worker && rolePermissionsHighLighters.includes(selectedHighlight?.name) ? (
                            <CustomTooltip id={selectedHighlight?.name.trim()} title="Permission Denied">
                                <Button type='button' disabled={is_Task_Worker} sx={{ marginLeft: "auto", padding: '8px 12px', borderRadius: "12px", cursor: "not-allowed" }}><FaPlus className="mx-1" />Add New</Button>
                            </CustomTooltip>
                        ) : (
                            <Button variant="SaveButton" onClick={handleDeliverClick} type="submit" sx={{ marginLeft: "auto", padding: '8px 12px', pointerEvents: is_Task_Worker ? "none" : "all" }}>+ Add New</Button>
                        )
                    )}
                </Flex>
                {groupedData && Object?.keys(groupedData)?.length > 0 ? (
                    Object?.keys(groupedData)?.map(stageName => {
                        const stageColor = groupedData[stageName]?.[0]?.stageId?.color || 'black';

                        return (

                            <Card key={stageName} sx={{ boxShadow: "none", background: modalHeader, border: `1px solid ${theme?.colors?.lightbackground}`, borderRadius: "12px", padding: "8px 16px", position: "relative", marginBottom: "20px", marginTop: '10px' }}>
                                <Flex sx={{ alignItems: "center", marginBottom: "20px" }}>
                                    <Badge sx={{
                                        color: stageColor,
                                        border: `1px solid ${stageColor}`,
                                        borderRadius: 6,
                                        backgroundColor: `${theme?.colors?.transparent}`,
                                        margin: "5px",
                                        fontSize: '20px'
                                    }}>
                                        {stageName}
                                    </Badge>

                                </Flex>

                                <DataTable
                                    loader={loader}
                                    columns={columns}
                                    data={groupedData[stageName]?.length > 0 ? groupedData[stageName] : []}
                                    perPageCount={paginations.limit}
                                    currentPage={paginations.currentPage}
                                    showNavigation={false}
                                    onPerPageChange={(itemsPerPage) => { }}
                                    onPageChange={(value) => { }}
                                />
                            </Card>
                        )
                    })
                ) : (
                    <Flex sx={{ justifyContent: "center", alignItems: "center", py: 4 }}>
                        <Text sx={{ color: titleColor, fontSize: "16px" }}>
                            {`No ${selectedHighlight?.name?.toLowerCase() || "revision"} is found`}
                        </Text>
                    </Flex>
                )}
            </ModalBody>
            <ModalFooter className='gap-2 justify-content-end' style={{ backgroundColor: modalHeader }}>
            </ModalFooter>

            <CreateBC selectedHighlight={selectedHighlight} Open={showCreateBCModal} setOpen={setShowCreateBCModal} actionObject={actionObject} setActionId={setActionId} />
            <Modal isOpen={showDeleteConfirmation} toggle={isDeleting ? undefined : cancelDeleteStage} size="sm" centered>
                <ModalHeader toggle={isDeleting ? undefined : cancelDeleteStage} style={{ backgroundColor: modalHeader, color: titleColor }}>Confirm Deletion</ModalHeader>
                <ModalBody style={{ backgroundColor: modalHeader, color: titleColor }}>
                    <Text>
                        {deleteConfirmMessage || `Are you sure you want to delete ${selectedDeleteRow?.original?.payload?.name ? selectedDeleteRow?.original?.payload?.name : "this action"} in this action?`}
                    </Text>
                </ModalBody>
                <ModalFooter style={{ backgroundColor: modalHeader }}>
                    <Button variant="CancelButton" sx={{ mr: 2, cursor: isDeleting ? 'not-allowed' : 'pointer' }} onClick={cancelDeleteStage} disabled={isDeleting}>No</Button>
                    <Button type="button" variant="SaveButton" sx={{ cursor: isDeleting ? 'not-allowed' : 'pointer' }} onClick={confirmDeleteAction} disabled={isDeleting}>
                        {isDeleting ? <Spinner size={16} sx={{ color: '#fff' }} /> : 'Yes'}
                    </Button>
                </ModalFooter>
            </Modal>
            <ActionBcPcChecklist Open={showActionBcPcChecklistModal} selectedHighlight={selectedHighlight} setOpen={() => setShowActionBcPcChecklistModal(false)} actionId={actionId} actionObject={actionObject} keys={keys} status={status} />

        </Modal>
    )
}

export default ActionBackgroundCheck